import { createSlice } from '@reduxjs/toolkit';
import {
  SupportSubjectRemoveUploadAction,
  SupportSubjectReopenVisitAction,
  SupportSubjectsListFetchAction,
  SupportSubjectsListFetchSuccessAction,
  SupportSubjectsListState,
} from '@restore/SupportSubject/SupportSubject.types';
import { createSliceHook, makeActionCreator } from '../helpers';
import {
  SupportQCTask,
  SupportReadingTask,
  SupportTaskDefinition,
  SupportUpload,
  VisitDate,
} from '@dynamika-shared/types/subjects';

const initialState: SupportSubjectsListState = {
  loading: false,
  error: undefined,
  data: undefined,
  newlyCreatedSubjectCode: null,
  search: '',

  uploadsByKey: {},
  uploadsLoading: false,
  uploadsError: undefined,

  taskDefinitionsByKey: {},
  taskDefinitionsLoading: false,
  taskDefinitionsError: undefined,

  readingTasksByKey: {},
  readingTasksLoading: false,
  readingTasksError: undefined,

  qcTasksByKey: {},
  qcTasksLoading: false,
  qcTasksError: undefined,

  visitDatesByKey: undefined,
  visitDatesLoading: false,
  visitDatesError: undefined,

  removingUploadById: {},

  reopeningTaskById: {},
  reopenValidationByTaskId: {},
  reopenActionErrorByTaskId: {},
};

const { act, actReject, actReq, actResolve } =
  makeActionCreator<SupportSubjectsListState>();

type RemoveUploadPayload = SupportSubjectRemoveUploadAction['payload'];
type ReopenTaskPayload = SupportSubjectReopenVisitAction['payload'];

export const SupportSubjectSlice = createSlice({
  name: 'SupportSubjectsList',
  initialState,
  reducers: {
    fetchSupportSubjectsList:
      actReq<SupportSubjectsListFetchAction['payload']>(),
    fetchSupportSubjectsListSuccess:
      actResolve<SupportSubjectsListFetchSuccessAction['payload']>(),
    fetchSupportSubjectsListFailure: actReject(),

    setSearchValue: act<string>(searchValue => ({
      search: searchValue,
    })),

    fetchSupportSubjectUploads: act<{
      clinicalTrialId: string;
      patientId: string;
    }>(() => ({
      uploadsLoading: true,
      uploadsError: undefined,
    })),
    fetchSupportSubjectUploadsSuccess: act<{
      key: string;
      data: SupportUpload[];
    }>((payload, state) => ({
      uploadsLoading: false,
      uploadsByKey: {
        ...(state.uploadsByKey ?? {}),
        [payload.key]: payload.data,
      },
    })),
    fetchSupportSubjectUploadsFailure: act<Error>(error => ({
      uploadsLoading: false,
      uploadsError: error,
    })),

    fetchSupportSubjectTaskDefinitions: act<{
      clinicalTrialId: string;
      patientId: string;
    }>(() => ({
      taskDefinitionsLoading: true,
      taskDefinitionsError: undefined,
    })),

    fetchSupportSubjectTaskDefinitionsSuccess: act<{
      key: string;
      data: SupportTaskDefinition[];
    }>((payload, state) => ({
      taskDefinitionsLoading: false,
      taskDefinitionsByKey: {
        ...(state.taskDefinitionsByKey ?? {}),
        [payload.key]: payload.data,
      },
    })),

    fetchSupportSubjectTaskDefinitionsFailure: act<Error>(error => ({
      taskDefinitionsLoading: false,
      taskDefinitionsError: error,
    })),

    fetchSupportSubjectReadingTasks: act<{
      clinicalTrialId: string;
      patientId: string;
    }>(() => ({
      readingTasksLoading: true,
      readingTasksError: undefined,
    })),

    fetchSupportSubjectReadingTasksSuccess: act<{
      key: string;
      data: SupportReadingTask[];
    }>((payload, state) => ({
      readingTasksLoading: false,
      readingTasksByKey: {
        ...(state.readingTasksByKey ?? {}),
        [payload.key]: payload.data,
      },
    })),

    fetchSupportSubjectReadingTasksFailure: act<Error>(error => ({
      readingTasksLoading: false,
      readingTasksError: error,
    })),

    fetchSupportSubjectQCTasks: act<{
      clinicalTrialId: string;
      patientId: string;
    }>(() => ({
      qcTasksLoading: true,
      qcTasksError: undefined,
    })),

    fetchSupportSubjectQCTasksSuccess: act<{
      key: string;
      data: SupportQCTask[];
    }>((payload, state) => ({
      qcTasksLoading: false,
      qcTasksByKey: {
        ...(state.qcTasksByKey ?? {}),
        [payload.key]: payload.data,
      },
    })),

    fetchSupportSubjectQCTasksFailure: act<Error>(error => ({
      qcTasksLoading: false,
      qcTasksError: error,
    })),

    fetchSupportSubjectVisitDates: act<{
      clinicalTrialId: string;
      patientId: string;
    }>(() => ({
      visitDatesLoading: true,
      visitDatesError: undefined,
    })),
    fetchSupportSubjectVisitDatesSuccess: act<{
      key: string;
      data: VisitDate;
    }>((payload, state) => ({
      visitDatesLoading: false,
      visitDatesByKey: {
        ...state.visitDatesByKey,
        [payload.key]: payload.data,
      },
    })),
    fetchSupportSubjectVisitDatesFailure: act<Error>(error => ({
      visitDatesLoading: false,
      visitDatesError: error,
    })),

    removeSupportSubjectUpload: act<RemoveUploadPayload>((payload, state) => ({
      removingUploadById: {
        ...(state.removingUploadById ?? {}),
        [payload.uploadId]: true,
      },
    })),

    removeSupportSubjectUploadSuccess: act<{
      key: string;
      uploadId: string;
    }>((payload, state) => {
      const { key, uploadId } = payload;

      const currentUploads = state.uploadsByKey?.[key] ?? [];
      const nextUploads = currentUploads.filter(
        u => u.uploadItemId !== uploadId
      );

      const removingUploadById = {
        ...(state.removingUploadById ?? {}),
      };
      delete removingUploadById[uploadId];

      return {
        uploadsByKey: {
          ...(state.uploadsByKey ?? {}),
          [key]: nextUploads,
        },
        removingUploadById,
      };
    }),

    removeSupportSubjectUploadFailure: act<{
      uploadId: string;
      error: Error;
    }>((payload, state) => {
      const { uploadId } = payload;

      const removingUploadById = {
        ...(state.removingUploadById ?? {}),
      };
      delete removingUploadById[uploadId];

      return {
        removingUploadById,
      };
    }),

    validateReopenSupportSubjectTask: act<{
      clinicalTrialId: string;
      taskId: string;
    }>((payload, state) => {
      const reopenActionErrorByTaskId = {
        ...(state.reopenActionErrorByTaskId ?? {}),
      };
      delete reopenActionErrorByTaskId[payload.taskId];

      return {
        reopenActionErrorByTaskId,
        reopenValidationByTaskId: {
          ...(state.reopenValidationByTaskId ?? {}),
          [payload.taskId]: {
            loading: true,
            errors: [],
            validated: false,
            error: undefined,
          },
        },
      };
    }),

    validateReopenSupportSubjectTaskSuccess: act<{
      taskId: string;
      errors: string[];
    }>((payload, state) => ({
      reopenValidationByTaskId: {
        ...(state.reopenValidationByTaskId ?? {}),
        [payload.taskId]: {
          loading: false,
          errors: payload.errors,
          validated: true,
          error: undefined,
        },
      },
    })),

    validateReopenSupportSubjectTaskFailure: act<{
      taskId: string;
      error: Error;
    }>((payload, state) => ({
      reopenValidationByTaskId: {
        ...(state.reopenValidationByTaskId ?? {}),
        [payload.taskId]: {
          loading: false,
          errors: [payload.error.message],
          validated: true,
          error: payload.error,
        },
      },
    })),

    clearReopenSupportSubjectTaskValidation: act<{ taskId: string }>(
      (payload, state) => {
        const reopenValidationByTaskId = {
          ...(state.reopenValidationByTaskId ?? {}),
        };
        const reopenActionErrorByTaskId = {
          ...(state.reopenActionErrorByTaskId ?? {}),
        };
        delete reopenValidationByTaskId[payload.taskId];
        delete reopenActionErrorByTaskId[payload.taskId];

        return { reopenValidationByTaskId, reopenActionErrorByTaskId };
      }
    ),

    reopenSupportSubjectTask: act<ReopenTaskPayload>((payload, state) => {
      const reopenActionErrorByTaskId = {
        ...(state.reopenActionErrorByTaskId ?? {}),
      };
      delete reopenActionErrorByTaskId[payload.taskId];

      return {
        reopenActionErrorByTaskId,
        reopeningTaskById: {
          ...(state.reopeningTaskById ?? {}),
          [payload.taskId]: true,
        },
      };
    }),

    reopenSupportSubjectTaskSuccess: act<{ taskId: string }>(
      (payload, state) => {
        const { taskId } = payload;

        const reopeningTaskById = {
          ...(state.reopeningTaskById ?? {}),
        };
        const reopenActionErrorByTaskId = {
          ...(state.reopenActionErrorByTaskId ?? {}),
        };
        delete reopeningTaskById[taskId];
        delete reopenActionErrorByTaskId[taskId];

        return { reopeningTaskById, reopenActionErrorByTaskId };
      }
    ),

    reopenSupportSubjectTaskFailure: act<{
      taskId: string;
      error: Error;
    }>((payload, state) => {
      const { taskId } = payload;

      const reopeningTaskById = {
        ...(state.reopeningTaskById ?? {}),
      };
      delete reopeningTaskById[taskId];

      return {
        reopeningTaskById,
        reopenActionErrorByTaskId: {
          ...(state.reopenActionErrorByTaskId ?? {}),
          [taskId]: payload.error.message,
        },
      };
    }),
  },
});

export const {
  fetchSupportSubjectsList,
  fetchSupportSubjectsListSuccess,
  fetchSupportSubjectsListFailure,

  fetchSupportSubjectUploads,
  fetchSupportSubjectUploadsSuccess,
  fetchSupportSubjectUploadsFailure,

  fetchSupportSubjectTaskDefinitions,
  fetchSupportSubjectTaskDefinitionsSuccess,
  fetchSupportSubjectTaskDefinitionsFailure,

  fetchSupportSubjectReadingTasks,
  fetchSupportSubjectReadingTasksSuccess,
  fetchSupportSubjectReadingTasksFailure,

  fetchSupportSubjectQCTasks,
  fetchSupportSubjectQCTasksSuccess,
  fetchSupportSubjectQCTasksFailure,

  fetchSupportSubjectVisitDates,
  fetchSupportSubjectVisitDatesSuccess,
  fetchSupportSubjectVisitDatesFailure,

  removeSupportSubjectUpload,
  removeSupportSubjectUploadSuccess,
  removeSupportSubjectUploadFailure,

  validateReopenSupportSubjectTask,
  validateReopenSupportSubjectTaskSuccess,
  validateReopenSupportSubjectTaskFailure,
  clearReopenSupportSubjectTaskValidation,

  reopenSupportSubjectTask,
  reopenSupportSubjectTaskSuccess,
  reopenSupportSubjectTaskFailure,
} = SupportSubjectSlice.actions;

export const actions = {
  ...SupportSubjectSlice.actions,
};

export const useSupportSubjectsListSlice = createSliceHook(
  SupportSubjectSlice.name,
  actions
);
